/* eslint-disable id-length */
import { GroupStep } from "app/lib/pipeline";
import BaseNode from "./BaseNode";
import { Node, NodeProps } from "@xyflow/react";
import * as HoverCard from "./StepHoverCard";
import { MissingStepDependencies } from "./MissingStepDependencies";
import { useBuild } from "app/components/build/Show/lib/BuildContext";
import Emojify from "app/components/shared/Emojify";
import { MouseEvent, useCallback } from "react";
import { useBuildStore } from "app/components/build/Show/lib/useBuildStore";
import Icon from "app/components/shared/Icon";
import { GroupStepCount } from "app/components/build/Show/components/GroupStepCount";
import classNames from "classnames";

type GroupStepNodeType = Node<GroupStep, "group">;

export default function GroupStepNode(props: NodeProps<GroupStepNodeType>) {
  const { missingDependencies = [] } = props.data;
  const { build } = useBuild({ strict: false });

  const { isGroupStepExpanded, expandGroupSteps, collapseGroupSteps } = useBuildStore();

  /**
   * Expand group step when clicking on the node.
   *
   * Don't collapse the group step when clicking on it, as this would be
   * counter-intuitive for users. Instead, we only expand the group step
   * when it is not already expanded.
   */
  const handleNodeClick = useCallback(() => {
    if (!isGroupStepExpanded(props.id)) {
      expandGroupSteps([props.id]);
    }
  }, [isGroupStepExpanded, expandGroupSteps, props.id]);

  const toggleGroup = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();

      isGroupStepExpanded(props.id) ? collapseGroupSteps([props.id]) : expandGroupSteps([props.id]);
    },
    [isGroupStepExpanded, expandGroupSteps, collapseGroupSteps, props.id],
  );

  return (
    <HoverCard.Root>
      <HoverCard.Portal>
        {build && missingDependencies.length && props.data.state && (
          <HoverCard.Content
            state={props.data.state}
            outcome={props.data.outcome}
            missingDependencies={props.data.missingDependencies}
          >
            <MissingStepDependencies missingDependencies={missingDependencies} build={build} />
          </HoverCard.Content>
        )}
      </HoverCard.Portal>

      <HoverCard.Trigger asChild={true}>
        <div className="h-full w-full max-w-none bg-white" onClick={handleNodeClick}>
          <BaseNode
            // For groups steps the node width and height is determined by ELK layout on parent element.
            // Therefore, we need to set width and height to 100% to allow the node to fill the available space.
            className={classNames("group h-full w-full max-w-none hover:no-underline", {
              "bg-opacity-40": isGroupStepExpanded(props.id),
            })}
            {...props}
            label={<Emojify text={props.data.label} />}
            count={build ? <GroupStepCount step={props.data} /> : undefined}
            actions={
              <button
                onClick={toggleGroup}
                className="flex items-center rounded-sm border-transparent bg-gray-100 text-xs text-charcoal-700 transition-transform duration-75 hover:border-purple-600 hover:bg-purple-100 hover:text-purple-600 active:translate-y-0.5 active:bg-purple-100 active:text-purple-600"
                title={isGroupStepExpanded(props.id) ? "Collapse" : "Expand"}
              >
                <Icon icon="heroicons/solid/chevron-up-down" className="block h-5 w-5" />
              </button>
            }
          />
        </div>
      </HoverCard.Trigger>
    </HoverCard.Root>
  );
}
